import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { Dialog2faCodeComponent } from '../../partials/components/dialog-2fa-code/dialog-2fa-code.component';
import { DialogConfirmComponent } from '../../partials/components/dialog-confirm/dialog-confirm.component';
import { HttpErrorCodes, TWO_FACTOR_HTTP } from '../../api/enums/http-error-codes.enum';
import { TwoFactorService } from '../../api/services/two-factor.service';
import { TWO_FACTOR_REQUIRED } from './two-factor.context';
import { SILENT_ERRORS } from './silent-errors.context';
import { httpErrorMessage } from './http-error-message';

// AML-627 — 2FA on critical admin actions, two complementary paths:
//   • Preventive: requests marked with TWO_FACTOR_REQUIRED + an enrolled admin → ask for the
//     code BEFORE sending and attach `X-2FA-Code`, so the popup shows on click (no extra round-trip).
//   • Reactive fallback: any request the backend gates with 428 → prompt then replay.
// Both share challenge(): dialog → replay with header; 422 re-prompts, 429 warns, cancel aborts.
export const twoFactorInterceptor: HttpInterceptorFn = (req, next) => {
    const dialog = inject(MatDialog);
    const snackBar = inject(MatSnackBar);
    const twoFactor = inject(TwoFactorService);
    const router = inject(Router);

    if (req.context.get(TWO_FACTOR_REQUIRED)) {
        // Critical action: enrolled → ask for the code; not enrolled → block and invite to set up 2FA.
        return twoFactor.enabled()
            ? challenge(req, next, dialog, snackBar, false, null)
            : promptEnroll(dialog, router);
    }

    return next(req).pipe(
        catchError((error: HttpErrorResponse) => {
            if (error.status !== TWO_FACTOR_HTTP.REQUIRED) {
                return throwError(() => error);
            }
            return challenge(req, next, dialog, snackBar, false, error);
        })
    );
};

// Not enrolled but tried a critical action — block it and offer to open the 2FA setup page.
function promptEnroll(dialog: MatDialog, router: Router): Observable<HttpEvent<unknown>> {
    return dialog.open(DialogConfirmComponent, {
        width: '100%',
        maxWidth: '420px',
        backdropClass: 'cdk-overlay-dark-backdrop',
        data: {
            variant: 'warning',
            title: 'Two-factor authentication required',
            description: 'This action requires two-factor authentication. Set up Google Authenticator for your account to continue.',
            confirmLabel: 'Set up 2FA',
        },
    }).afterClosed().pipe(
        switchMap((goToSetup: boolean) => {
            if (goToSetup) {
                // Close any dialogs left open by the action (e.g. the top-up dialog) before leaving.
                dialog.closeAll();
                router.navigate(['/security']);
            }
            const err = new HttpErrorResponse({
                status: TWO_FACTOR_HTTP.REQUIRED,
                error: { code: HttpErrorCodes.TwoFactorRequired },
            });
            markHandled(err);
            return throwError(() => err);
        })
    );
}

function challenge(
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    dialog: MatDialog,
    snackBar: MatSnackBar,
    showError: boolean,
    originalError: HttpErrorResponse | null,
): Observable<HttpEvent<unknown>> {
    const ref = dialog.open(Dialog2faCodeComponent, {
        width: '420px',
        maxWidth: '95vw',
        backdropClass: 'cdk-overlay-dark-backdrop',
        data: { error: showError },
    });

    return ref.afterClosed().pipe(
        switchMap((code: string | null | undefined) => {
            // Cancelled → the action must not run. Rethrow a handled error so no generic snackbar shows.
            if (!code) {
                const err = originalError ?? new HttpErrorResponse({
                    status: TWO_FACTOR_HTTP.REQUIRED,
                    error: { code: HttpErrorCodes.TwoFactorRequired },
                });
                markHandled(err);
                return throwError(() => err);
            }

            const retried = req.clone({ setHeaders: { 'X-2FA-Code': code } });
            return next(retried).pipe(
                catchError((err: HttpErrorResponse) => {
                    // Wrong / expired code — ask again. Only for code 38 (or no code): other 422s are the
                    // action's own refusal (e.g. deposit replay's "no such transaction", AML-1005) and
                    // re-prompting would loop the admin through fresh codes for an answer that won't change.
                    const errCode = (err.error as { code?: unknown } | null)?.code;
                    if (err.status === TWO_FACTOR_HTTP.INVALID && (errCode == null || errCode === HttpErrorCodes.TwoFactorInvalidCode)) {
                        return challenge(req, next, dialog, snackBar, true, originalError);
                    }
                    if (err.status === TWO_FACTOR_HTTP.TOO_MANY) {
                        snackBar.open('Too many attempts. Please try again later.', 'Close');
                        markHandled(err);
                        return throwError(() => err);
                    }
                    // The replay is sent from inside the chain, so `errorInterceptor` — which sits
                    // upstream — never sees what comes back. Every 2FA-gated action that the backend
                    // then refuses used to fail in silence: deactivating your own admin account
                    // rolled the toggle back with no word of why (AML-1056). Say it here instead.
                    if (!req.context.get(SILENT_ERRORS)) {
                        snackBar.open(httpErrorMessage(err), 'Close');
                        markHandled(err);
                    }
                    return throwError(() => err);
                })
            );
        })
    );
}

// Flag so errorInterceptor doesn't also pop a generic snackbar for an error we already own.
function markHandled(error: HttpErrorResponse): void {
    (error as unknown as { twoFactorHandled?: boolean }).twoFactorHandled = true;
}
